"use client";

import { replaceStateAndNotify } from "@shared/lib/history/dispatch-url-change";

/** Section ids linked from the marketing home nav (`/#…`). Order matches scroll position top → bottom. */
export const MARKETING_HOME_NAV_SECTION_IDS = [
	"problems",
	"mission",
	"solution",
	"features",
	"testimonials",
	"earlyaccess",
	"faqs",
] as const;

export type MarketingHomeNavSectionId =
	(typeof MARKETING_HOME_NAV_SECTION_IDS)[number];

export function isMarketingHomeNavSectionId(
	id: string,
): id is MarketingHomeNavSectionId {
	return (MARKETING_HOME_NAV_SECTION_IDS as readonly string[]).includes(id);
}

/** Updates the URL hash without adding a history entry (scroll spy / same-document navigation). */
export function replaceMarketingLandingHash(sectionId: string | null): void {
	if (typeof window === "undefined") {
		return;
	}
	const base = `${window.location.pathname}${window.location.search}`;
	const url = sectionId ? `${base}#${sectionId}` : base;
	replaceStateAndNotify(url, {
		reason: "scroll-spy",
		source: "marketing-landing-section",
	});
}

export function parseMarketingLandingHash(): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	const raw = window.location.hash.slice(1);
	if (raw === "") {
		return null;
	}
	if (raw === "cta") {
		return "earlyaccess";
	}
	return isMarketingHomeNavSectionId(raw) ? raw : null;
}

export function marketingNavSectionIdFromHashHref(href: string): string | null {
	if (!href.startsWith("/#")) {
		return null;
	}
	const id = href.slice(2);
	return isMarketingHomeNavSectionId(id) ? id : null;
}

export function computeActiveMarketingNavSectionId(
	headerBottomPx: number,
): string | null {
	let active: string | null = null;
	for (const id of MARKETING_HOME_NAV_SECTION_IDS) {
		const el = document.getElementById(id);
		if (!el) {
			continue;
		}
		const top = el.getBoundingClientRect().top;
		if (top <= headerBottomPx + 8) {
			active = id;
		}
	}
	return active;
}

export function marketingSiteHeaderBottomPx(): number {
	const header = document.querySelector("[data-marketing-site-header]");
	return header instanceof HTMLElement
		? Math.ceil(header.getBoundingClientRect().bottom)
		: 80;
}
